import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Ati } from '@atlassian/ari';
import { DEFAULT_STATSIG_PARALLEL_EXPERIMENTS_CONFIG, STATSIG_SEARCH_BACKEND_FEATURE_GATE_L1_STANDARD_VS_PREMIUM, STATSIG_SEARCH_BACKEND_FEATURE_GATE_L2_STANDARD_VS_PREMIUM, STATSIG_SEARCH_BACKEND_FEATURE_GATE_L3_ENABLED, STATSIG_SEARCH_BACKEND_FEATURE_GATE_QUERY_EXPANSION_ENABLED, STATSIG_SEARCH_BACKEND_L1_LAYER_PREMIUM, STATSIG_SEARCH_BACKEND_L1_LAYER_STANDARD, STATSIG_SEARCH_BACKEND_L2_BLENDING_LAYER, STATSIG_SEARCH_BACKEND_L2_LAYER_PREMIUM, STATSIG_SEARCH_BACKEND_L2_LAYER_STANDARD, STATSIG_SEARCH_BACKEND_L3_LAYER, STATSIG_SEARCH_BACKEND_NON_OVERLAPPING_DOMAIN, STATSIG_SEARCH_BACKEND_QUERY_EXPANSION_LAYER } from '../../constants';
import { mapCombinedLayerExperiment, mapDynamicExperiments, mapNonOverlappingBackendExperiment } from '../dynamic';
export var getBackendExperimentConfig = function getBackendExperimentConfig(onErrorFn) {
  var shouldNotGetBackendExperiment = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var isRequestDrivenExperimentationEnabled = FeatureGates.checkGate('request_driven_experimentation_enabled');
  if (!isRequestDrivenExperimentationEnabled || shouldNotGetBackendExperiment) {
    return;
  }
  return getParallelBackendExperimentsConfig(onErrorFn);
};
export var getLayerValues = function getLayerValues(layerName) {
  var layer = FeatureGates.getLayer(layerName);
  var layerNameWithoutSuffix = layerName.replace('_standard', '').replace('_premium', '');
  return _objectSpread({
    name: layerNameWithoutSuffix,
    layerId: layer.get('layerId', 'default'),
    shadowId: layer.get('shadowId', null),
    // The below values might not be sent to the backend but we can still track them in the frontend
    controlId: layer.get('controlId', null),
    abTestId: layer.get('abTestId', 'no_ab_test_id')
  }, FeatureGates.checkGate('search-experiment-overrides-backend') && {
    testId: layer.get('testId', null)
  });
};
export var getBackendNonOverlappingDomainLayerValues = function getBackendNonOverlappingDomainLayerValues(onError) {
  try {
    var layer = FeatureGates.getLayer(STATSIG_SEARCH_BACKEND_NON_OVERLAPPING_DOMAIN);
    var abTestId = layer.get('abTestId', 'no_ab_test_id');
    return {
      l1Id: layer.get('l1Id', 'default'),
      l2Id: layer.get('l2Id', 'default'),
      l3Id: layer.get('l3Id', 'default'),
      qrId: layer.get('qrId', 'default'),
      abTestId: abTestId,
      // The get 'mutualExclusion' is breaking in Jira when retrieving a boolean value from layer,
      // so for the compatibility, we use the abTestId to determine if it's a mutually exclusive experiment
      mutualExclusion: abTestId !== 'no_ab_test_id',
      controlId: layer.get('controlId', 'default')
    };
  } catch (error) {
    onError === null || onError === void 0 || onError(error);
  }
};
export var getCombinedLayerValues = function getCombinedLayerValues(layerName, onError) {
  try {
    var layer = FeatureGates.getLayer(layerName);
    var layerValues = {
      l1Id: layer.get('l1Id', 'default'),
      l2Id: layer.get('l2Id', 'default'),
      l3Id: layer.get('l3Id', 'default'),
      qrId: layer.get('qrId', 'default'),
      abTestId: layer.get('abTestId', 'no_ab_test_id'),
      /**
       * This is a map of UI features that are enabled for the combined experiment.
       * for example:
       * {
       * 	uiFeature1: true,
       * 	uiFeature2: false,
       * }
       */
      uiFeatures: layer.get('uiFeatures', {}),
      controlId: layer.get('controlId', 'default'),
      enabled: layer.get('enabled', false)
    };
    return layerValues;
  } catch (error) {
    onError === null || onError === void 0 || onError(error);
  }
};
var getStandardOrPremiumLayerValues = function getStandardOrPremiumLayerValues(featureGateName, standardLayerName, premiumLayerName) {
  var isPremium = FeatureGates.checkGate(featureGateName);
  if (isPremium) {
    return getLayerValues(premiumLayerName);
  }
  return getLayerValues(standardLayerName);
};
var getParallelBackendExperimentsConfig = function getParallelBackendExperimentsConfig(onErrorFn) {
  var isRequestDrivenExperimentationEnabled = FeatureGates.checkGate('request_driven_experimentation_enabled');
  if (!isRequestDrivenExperimentationEnabled) {
    return;
  }
  try {
    var allLayers = [];
    var layer1 = getStandardOrPremiumLayerValues(STATSIG_SEARCH_BACKEND_FEATURE_GATE_L1_STANDARD_VS_PREMIUM, STATSIG_SEARCH_BACKEND_L1_LAYER_STANDARD, STATSIG_SEARCH_BACKEND_L1_LAYER_PREMIUM);
    allLayers.push(layer1);
    var layer2 = getStandardOrPremiumLayerValues(STATSIG_SEARCH_BACKEND_FEATURE_GATE_L2_STANDARD_VS_PREMIUM, STATSIG_SEARCH_BACKEND_L2_LAYER_STANDARD, STATSIG_SEARCH_BACKEND_L2_LAYER_PREMIUM);
    allLayers.push(layer2);
    var layer2Blending = getLayerValues(STATSIG_SEARCH_BACKEND_L2_BLENDING_LAYER);
    allLayers.push(layer2Blending);
    var isLayer3Enabled = FeatureGates.checkGate(STATSIG_SEARCH_BACKEND_FEATURE_GATE_L3_ENABLED);
    if (isLayer3Enabled) {
      var layer3 = getLayerValues(STATSIG_SEARCH_BACKEND_L3_LAYER);
      allLayers.push(layer3);
    }
    var isQueryExpansionLayerEnabled = FeatureGates.checkGate(STATSIG_SEARCH_BACKEND_FEATURE_GATE_QUERY_EXPANSION_ENABLED);
    if (isQueryExpansionLayerEnabled) {
      var layerQueryExpansion = getLayerValues(STATSIG_SEARCH_BACKEND_QUERY_EXPANSION_LAYER);
      allLayers.push(layerQueryExpansion);
    }
    var concatenatedExperimentId = allLayers.map(function (l) {
      return l === null || l === void 0 ? void 0 : l.layerId;
    }).join('_');
    var concatenatedShadowExperimentId = allLayers.map(function (l) {
      return l === null || l === void 0 ? void 0 : l.shadowId;
    }).join('_');
    return {
      experimentId: concatenatedExperimentId,
      shadowExperimentId: concatenatedShadowExperimentId,
      experimentLayers: allLayers
    };
  } catch (error) {
    onErrorFn === null || onErrorFn === void 0 || onErrorFn(error);
  }
  return DEFAULT_STATSIG_PARALLEL_EXPERIMENTS_CONFIG;
};
export var getBackendExperiment = function getBackendExperiment(_ref) {
  var _experimentConfig$com;
  var experimentConfig = _ref.experimentConfig,
    entities = _ref.entities,
    productEdition = _ref.productEdition,
    isHello = _ref.isHello,
    productDataRegions = _ref.productDataRegions,
    experience = _ref.experience,
    tenantId = _ref.tenantId,
    isJiraIssueIndexed = _ref.isJiraIssueIndexed;
  if ((_experimentConfig$com = experimentConfig.combinedExperiment) !== null && _experimentConfig$com !== void 0 && _experimentConfig$com.enabled) {
    return mapCombinedLayerExperiment({
      combinedExperiment: experimentConfig.combinedExperiment
    });
  }
  if (fg('search-experiment-overrides-backend')) {
    return experimentConfig.backendExperiment;
  }
  if (!experimentConfig.dynamicExperiments || experimentConfig.isMutuallyExclusive) {
    return undefined;
  }
  var requestedEntities = entities.flatMap(function (entity) {
    try {
      var _Ati$parse = Ati.parse(entity),
        resourceOwner = _Ati$parse.resourceOwner,
        resourceType = _Ati$parse.resourceType;
      return [{
        entity: resourceOwner,
        subEntity: resourceType.toUpperCase()
      }];
    } catch (_unused) {
      return [];
    }
  });
  if (fg('search_platform_non_overlapping_domain')) {
    var _experimentConfig$non;
    if ((_experimentConfig$non = experimentConfig.nonOverlappingBackendExperiment) !== null && _experimentConfig$non !== void 0 && _experimentConfig$non.mutualExclusion) {
      return mapNonOverlappingBackendExperiment({
        nonOverlappingExperiment: experimentConfig.nonOverlappingBackendExperiment
      });
    }
  }
  return mapDynamicExperiments({
    dynamicExperiments: experimentConfig.dynamicExperiments,
    productEdition: productEdition,
    isHello: isHello,
    requestedEntities: requestedEntities,
    productDataRegions: productDataRegions,
    experience: experience,
    tenantId: tenantId,
    isJiraIssueIndexed: isJiraIssueIndexed
  });
};